import React, { useState } from 'react';
import { fmtDate, fmtDateTime } from '@shared/formatDate';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Link,
  Tabs,
  Tab,
  Tooltip,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import GitHubIcon from '@mui/icons-material/GitHub';
import LanguageIcon from '@mui/icons-material/Language';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { ProfileDto, ProfileAliasDto } from '../../services/socialService';
import { useAuth } from '../../hooks/useAuth';
import RichTextDisplay from './RichTextDisplay';
import { ProfileCoverBanner, ProfileMediaAvatar } from './ProfileMediaImage';

interface ProfileViewProps {
  profile: ProfileDto;
  isOwner: boolean;
  onEdit?: () => void;
  /** When provided, content tabs (Personal/Professional/etc.) are controlled by parent (e.g. to open editor on same tab). */
  contentTabValue?: number;
  onContentTabChange?: (tab: number) => void;
  /** When provided (owner view), show other aliases as links and "Manage Aliases" link in header. */
  aliases?: ProfileAliasDto[];
  selectedAliasId?: string;
  onSelectAlias?: (alias: ProfileAliasDto) => void;
  onOpenManageAliases?: () => void;
  /** When provided and 2 (Private), show private notice instead of profile visibility. */
  selectedAliasType?: number;
}

function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index } = props;
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const getPlatformIcon = (platform: string) => {
  const platformLower = platform.toLowerCase();
  if (platformLower.includes('twitter')) return <TwitterIcon />;
  if (platformLower.includes('linkedin')) return <LinkedInIcon />;
  if (platformLower.includes('facebook')) return <FacebookIcon />;
  if (platformLower.includes('instagram')) return <InstagramIcon />;
  if (platformLower.includes('github')) return <GitHubIcon />;
  return <LanguageIcon />;
};

const visibilityLabel = (level: number) => {
  switch (level) {
    case 1: return 'Public - Anyone can see your profile';
    case 2: return 'Friends Only';
    case 3: return 'Private - This profile is not viewable or searchable.';
    default: return 'Public';
  }
};

const ProfileView: React.FC<ProfileViewProps> = ({
  profile,
  isOwner,
  onEdit,
  contentTabValue,
  onContentTabChange,
  aliases,
  selectedAliasId,
  onSelectAlias,
  onOpenManageAliases,
  selectedAliasType,
}) => {
  const [internalTab, setInternalTab] = useState(0);
  const isControlled = contentTabValue !== undefined && onContentTabChange !== undefined;
  const currentTab = isControlled ? contentTabValue : internalTab;
  const setCurrentTab = isControlled ? onContentTabChange : setInternalTab;
  const { user } = useAuth();
  const baseUsername = (user?.username ?? '').trim().toLowerCase();
  // Login alias = the one you sign in with (ChrAliasName matches username). Others use chosen display/alias name.
  const selectedAlias = aliases && selectedAliasId !== undefined ? aliases.find((a) => a.idAliasID === selectedAliasId) : undefined;
  const isSelectedLoginAlias = baseUsername && selectedAlias && (selectedAlias.chrAliasName || '').trim().toLowerCase() === baseUsername;
  const displayName = isSelectedLoginAlias && selectedAlias
    ? (selectedAlias.chrBaseUserName || selectedAlias.chrAliasName || '').trim()
    : (profile.chrDisplayName || profile.chrAliasName);
  const displayHandle = isSelectedLoginAlias && selectedAlias
    ? (selectedAlias.chrAliasName || selectedAlias.chrBaseUserName || '').trim()
    : profile.chrAliasName;

  return (
    <Box>
      {/* Cover Image - only show if URL exists */}
      {profile.chrCoverImageURL && (
        <ProfileCoverBanner mediaUrl={profile.chrCoverImageURL} minHeight={200} />
      )}

      {/* Profile header + Tabs */}
      <Card sx={{ mt: profile.chrCoverImageURL ? -8 : 0, mx: 0, position: 'relative' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ProfileMediaAvatar
              mediaUrl={profile.chrProfileImageURL}
              sx={{
                width: 120,
                height: 120,
                border: '4px solid white',
                boxShadow: 2,
                fontSize: '0.7rem',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 1
              }}
            >
              {!profile.chrProfileImageURL && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                  Your picture goes here
                </Typography>
              )}
            </ProfileMediaAvatar>
            <Box sx={{ ml: 3, flex: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  {displayName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  @{displayHandle}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={(_e, v: number) => setCurrentTab(v)} variant="scrollable" scrollButtons="auto" sx={{ flex: 1 }}>
              <Tab icon={<PersonIcon />} iconPosition="start" label="Personal" />
              <Tab icon={<WorkIcon />} iconPosition="start" label="Professional" />
              <Tab icon={<LanguageIcon />} iconPosition="start" label="Social Media" />
              <Tab icon={<FavoriteIcon />} iconPosition="start" label="Interests & Hobbies" />
            </Tabs>
            {isOwner && onEdit && (
              <Tooltip title="Edit profile">
                <IconButton onClick={onEdit} aria-label="Edit profile" size="small" sx={{ mr: 0.5 }}>
                  <EditIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Personal */}
          <TabPanel value={currentTab} index={0}>
            {profile.txtBio && (
              <RichTextDisplay html={profile.txtBio} />
            )}
            {profile.chrLocation && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Location: {profile.chrLocation}
              </Typography>
            )}
            
            {/* Basic Information section */}
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Basic Information
              </Typography>
              {profile.dtmCreateDate && (
                <Typography variant="body2" color="text.secondary">
                  Created: {fmtDate(profile.dtmCreateDate)}
                </Typography>
              )}
              {/* Show "Created by" for business accounts (intAliasType === 4) */}
              {profile.intAliasType === 4 && profile.createdByUsername && (
                <Typography variant="body2" color="text.secondary">
                  Created by: {profile.createdByUsername}
                </Typography>
              )}
              {profile.businessName && (
                <Typography variant="body2" color="text.secondary">
                  Business Name: {profile.businessName}
                </Typography>
              )}
              {profile.credentialType && (
                <Typography variant="body2" color="text.secondary">
                  Credential Type: {profile.credentialType}
                </Typography>
              )}
            </Box>

            {!profile.txtBio && !profile.chrLocation && !profile.dtmCreateDate && profile.intVisibilityLevel === 1 && selectedAliasType !== 2 && (
              <Typography variant="body2" color="text.secondary">
                No personal details yet.
              </Typography>
            )}
          </TabPanel>

          {/* Professional */}
          <TabPanel value={currentTab} index={1}>
            <Stack spacing={2}>
              {(profile.chrJobTitle || profile.chrCompany) && (
                <Box>
                  {profile.chrJobTitle && (
                    <Typography variant="subtitle1">{profile.chrJobTitle}</Typography>
                  )}
                  {profile.chrCompany && (
                    <Typography variant="body2" color="text.secondary">{profile.chrCompany}</Typography>
                  )}
                </Box>
              )}
              {profile.chrWebsiteURL && (
                <Link
                  href={profile.chrWebsiteURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'block' }}
                >
                  Website: {profile.chrWebsiteURL}
                </Link>
              )}
              {profile.txtSkills && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Skills &amp; Expertise</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {profile.txtSkills.split(',').map((s) => s.trim()).filter(Boolean).map((skill) => (
                      <Chip key={skill} label={skill} size="small" />
                    ))}
                  </Stack>
                </Box>
              )}
              {profile.chrEducation && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Education</Typography>
                  <Typography variant="body2">{profile.chrEducation}</Typography>
                </Box>
              )}
              {profile.txtProfessionalBio && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Professional Bio</Typography>
                  <RichTextDisplay html={profile.txtProfessionalBio} />
                </Box>
              )}
              {!profile.chrJobTitle && !profile.chrCompany && !profile.chrWebsiteURL && !profile.txtSkills && !profile.chrEducation && !profile.txtProfessionalBio && (
                <Typography variant="body2" color="text.secondary">
                  No professional details added yet.
                </Typography>
              )}
            </Stack>
          </TabPanel>

          {/* Social Media */}
          <TabPanel value={currentTab} index={2}>
            {profile.socialLinks && profile.socialLinks.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {profile.socialLinks.map((link) => (
                  <IconButton
                    key={link.idSocialLinkID}
                    component="a"
                    href={link.chrPlatformURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    {getPlatformIcon(link.chrPlatform)}
                  </IconButton>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No social links added yet.
              </Typography>
            )}
          </TabPanel>

          {/* Interests & Hobbies */}
          <TabPanel value={currentTab} index={3}>
            {profile.interests && profile.interests.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {profile.interests.map((interest) => (
                  <Chip
                    key={interest.idInterestID}
                    label={interest.chrInterestTag}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No interests added yet.
              </Typography>
            )}
          </TabPanel>

          {isOwner && onEdit && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <IconButton onClick={onEdit} color="primary" size="small" sx={{ gap: 0.5 }}>
                <EditIcon fontSize="small" />
                <Typography variant="body2">Edit profile</Typography>
              </IconButton>
              <Chip
                size="small"
                label={selectedAliasType === 2 ? 'Private - This profile is not viewable or searchable.' : visibilityLabel(profile.intVisibilityLevel)}
              />
            </Box>
          )}
        </CardContent>
      </Card>

{/* Referral links now shown in header area above username */}
    </Box>
  );
};

export default ProfileView;

